<template>
  <div class="payment-container">
    <v-expansion-panel popout light v-if="route !== 'Dashboard' && route !== 'Availability'" class="futuristic-expansion">
      <v-expansion-panel-content>
        <template v-slot:header>
          <div>
            <h4 class="earth-color">MY BOOKINGS HISTORY</h4>
            <div class="title-decoration"></div>
          </div>
        </template>
        <template v-slot:actions>
          <v-icon color="#587e27">$vuetify.icons.expand</v-icon>
        </template>
        <v-card flat class="futuristic-card">
          <v-container fluid>
            <v-layout row wrap>
              <v-flex lg2 md3 sm4 xs12>
                <v-container fluid>
                  <v-autocomplete label="Status" v-model="booking_status_filter" :items="statusFilters" class="futuristic-input"></v-autocomplete>
                </v-container>
              </v-flex>
              <v-flex lg2 md3 sm4 xs12>
                <v-container fluid>
                  <v-autocomplete label="Date Range" v-model="date_filter" :items="dateFilters" class="futuristic-input"></v-autocomplete>
                </v-container>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <v-card flat class="futuristic-card">
      <v-card-title class="card-header">
        <div>
          <h4 class="earth-color">MY BOOKINGS ({{ filteredBookings.length }})</h4>
          <div class="title-decoration"></div>
          <!-- <div class="identifier-info" v-if="userEmail || sessionId">
            <small class="text-muted" v-if="sessionId">
              <span class="badge badge-session">🔑 Session ID: {{ sessionId.substring(0, 12) }}...</span>
            </small>
            <small class="text-muted ml-2" v-if="userEmail">
              <span class="badge badge-email">📧 Email: {{ userEmail }}</span>
            </small> 
          </div> -->
        </div>
        <v-spacer></v-spacer>
        <v-text-field append-icon="search" label="Search" single-line hide-details v-model="search" class="futuristic-search"></v-text-field>
      </v-card-title>
      
      <v-data-table :headers="columns" :items="filteredBookings" :search="search" :rows-per-page-items="[10, 25, 50, { text: 'All', value: -1 }]" :disable-initial-sort="true" :loading="$loadingTrue" class="futuristic-table">
        <v-progress-linear slot="progress" color="#587e27" indeterminate></v-progress-linear>
        
        <template v-slot:items="props">
          <td data-label="Created At">{{ formatDateTime(props.item.created_at) }}</td>
          <td data-label="Booking Ref">
            <router-link :to="{ name: 'Booking', params: { booking_id: props.item.booking_public_id }}">
              <a class="futuristic-link text-dark"><u>{{ props.item.booking_ref_code }}</u></a>
            </router-link>
          </td>
          <td data-label="Client Name">{{ props.item.session_user || props.item.booking_done_by || 'N/A' }}</td>
          <td data-label="Entry Pax">{{ props.item.guest_total || 0 }}</td>
          <td data-label="Accommodation">{{ getAccommodationNames(props.item) }}</td>
          <td data-label="Activity">{{ getActivityNames(props.item) }}</td>
          <td data-label="Check In">{{ formatDate(props.item.booking_check_in_date) }}</td>
          <td data-label="Check Out">{{ formatDate(props.item.booking_check_out_date) }}</td>
          <td data-label="Booking Amount">{{ props.item.booking_currency_name || props.item.booking_currency || 'KES' }} {{ formatPrice(props.item.total_cost || 0) }}</td>
          <td data-label="Booking Status"><span :style="getStatusStyle(props.item)">{{ getBookingStatus(props.item) }}</span></td>
          <td data-label="Action">
            <v-btn :depressed="true" small @click="viewBooking(props.item.booking_public_id)" class="futuristic-btn">View Booking</v-btn>
          </td>
        </template>
        
        <v-alert slot="no-results" :value="true" color="error" icon="warning" class="futuristic-alert">Your search for "{{ search }}" found no results.</v-alert>
        <v-alert slot="no-data" :value="true" color="info" icon="info" class="futuristic-alert">No bookings found.</v-alert>
      </v-data-table>
      
      <div class="text-xs-center" v-if="myBookings.length <= 0 && $loadingTrue">
        <ball-clip-rotate-pulse-loader color="#587e27" size="50px"></ball-clip-rotate-pulse-loader>
      </div>
      
      <v-layout row wrap class="mt-3" v-if="myBookings.length > 0">
        <v-flex xs12 sm6>
          <v-btn v-if="hasMore" :depressed="true" @click="getMore()" color="#587e27" :loading="loading" class="futuristic-btn">GET MORE</v-btn>
          <v-btn v-if="hasMore" :depressed="true" @click="getAllBookings()" color="#e84118" :loading="loading" class="futuristic-btn ml-2">GET ALL</v-btn>
        </v-flex>
        <v-flex xs12 sm6 class="text-xs-right">
          <vue-json-excel type="xls" worksheet="My Bookings Data" :name="getExportFileName('xls')" :data="filteredBookings" :fields="reportFields">
            <v-btn color="#27ae60" :depressed="true" class="futuristic-btn">EXPORT EXCEL</v-btn>
          </vue-json-excel>
          <vue-json-excel type="csv" worksheet="My Bookings Data" :name="getExportFileName('csv')" :data="filteredBookings" :fields="reportFields" class="ml-2">
            <v-btn color="#f39c12" :depressed="true" class="futuristic-btn">EXPORT CSV</v-btn>
          </vue-json-excel>
        </v-flex>
      </v-layout>
    </v-card>
  </div>
</template>

<script>
import VueJsonExcel from "vue-json-excel";

export default {
  name: 'MyBookingsHistory',
  components: { VueJsonExcel },
  
  data() {
    return {
      search: '',
      page: 2,
      loading: false,
      route: this.$route.name,
      booking_status_filter: null,
      date_filter: null,
      initialLoadDone: false,
      loadingError: null,
      statusFilters: ['All', 'Abandoned', 'Confirmed', 'Checked In', 'Checked Out', 'Cancelled'],
      dateFilters: ['All', 'This Week', 'This Month', 'Last 3 Months', 'This Year'],
      columns: [
        { text: 'Created At', value: 'created_at' },
        { text: 'Booking Ref', value: 'booking_ref_code' },
        { text: 'Client Name', value: 'session_user' },
        { text: 'Entry Pax', value: 'guest_total' },
        { text: 'Accommodation', value: 'accommodation' },
        { text: 'Activity', value: 'activity' },
        { text: 'Check In', value: 'booking_check_in_date' },
        { text: 'Check Out', value: 'booking_check_out_date' },
        { text: 'Booking Amount', value: 'total_cost' },
        { text: 'Booking Status', value: 'status' },
        { text: 'Action', value: '' }
      ],
      reportFields: {
        Created_At: { field: "created_at", callback: (date) => this.reportDateFormat(date) },
        Booking_Ref: "booking_ref_code",
        Client_Name: { field: "session_user", callback: (name) => name || "N/A" },
        Entry_Pax: "guest_total",
        Accommodation: { field: "facility_bookings", callback: (facilities) => { if (facilities && facilities.length > 0) { return facilities.map(f => f.facility_name).join(", "); } return "N/A"; } },
        Activity: { field: "inventory_bookings", callback: (inventories) => { if (inventories && inventories.length > 0) { return inventories.map(i => i.inventory_name).join(", "); } return "N/A"; } },
        Check_In_Date: { field: "booking_check_in_date", callback: (date) => this.reportDateFormat(date) },
        Check_Out_Date: { field: "booking_check_out_date", callback: (date) => this.reportDateFormat(date) },
        Booking_Amount: "total_cost",
        Currency: "booking_currency_name",
        Booking_Status: { field: "booking_status_display", callback: (status) => status || "Confirmed" },
        Source: "session_user"
      }
    }
  },
  
  computed: {
    userData() {
      try {
        const unem = localStorage.getItem('unem');
        return unem ? JSON.parse(unem) : null;
      } catch (e) {
        return null;
      }
    },
    userEmail() {
      const userEmail = localStorage.getItem('user_email');
      if (userEmail) return userEmail;
      const user = this.userData;
      if (user && user.email) return user.email;
      try {
        const userData = JSON.parse(localStorage.getItem('user_data') || '{}');
        if (userData && userData.email) return userData.email;
      } catch (e) {}
      return null;
    },
    sessionId() {
      return localStorage.getItem('lkjhgfdsa');
    },
    bookingIdentifier() {
      const identifier = {};
      if (this.userEmail) identifier.email = this.userEmail;
      if (this.sessionId) identifier.session_id = this.sessionId;
      return identifier;
    },
    hasIdentifiers() {
      return !!(this.userEmail && this.sessionId);
    },
    myBookings() {
      var bookings = [];
      if (this.$store.state.BookingStore && this.$store.state.BookingStore.myBookings) {
        bookings = this.$store.state.BookingStore.myBookings;
      } else if (this.$store.state.myBookings) {
        bookings = this.$store.state.myBookings;
      } else if (this.$store.state.bookings) {
        bookings = this.$store.state.bookings;
      }
      return bookings;
    },
    filteredBookings() {
      var filtered = [].concat(this.myBookings);
      if (this.booking_status_filter && this.booking_status_filter !== 'All') {
        filtered = filtered.filter(function(booking) {
          return this.getBookingStatus(booking) === this.booking_status_filter;
        }.bind(this));
      }
      if (this.date_filter && this.date_filter !== 'All') {
        filtered = this.filterByDateRange(filtered, this.date_filter);
      }
      return filtered;
    },
    hasMore() {
      var state = this.$store.state;
      var total = 0;
      if (state.BookingStore) {
        total = state.BookingStore.myBookingsTotal || 0;
      } else if (state.myBookingsTotal) {
        total = state.myBookingsTotal;
      }
      return this.myBookings.length >= 5 && this.myBookings.length < total;
    }
  },
  
  created() {
    this.loadBookings();
  },
  
  activated() {
    if (this.hasIdentifiers && this.myBookings.length === 0) {
      this.loadBookings();
    }
  },
  
  methods: {
    loadBookings() {
      const identifier = this.bookingIdentifier;
      if (!this.userEmail) {
        this.loadingError = 'No email found. Please login again.';
        return;
      }
      if (!this.sessionId) {
        this.loadingError = 'No session ID found. Please login again.';
        return;
      }
      if (this.myBookings.length === 0 || !this.initialLoadDone) {
        var _this = this;
        this.$store.dispatch('get_my_bookings', identifier)
          .then(function() {
            _this.initialLoadDone = true;
            _this.loadingError = null;
          })
          .catch(function() {
            _this.loadingError = 'Failed to load bookings. Please try again.';
          });
      }
    },
    resetAndReload() {
      this.page = 2;
      this.search = '';
      this.booking_status_filter = null;
      this.date_filter = null;
      this.loadingError = null;
      if (this.$store.state.BookingStore) {
        this.$store.commit('BookingStore/setMyBookings', []);
        this.$store.commit('BookingStore/setMyBookingsTotal', 0);
      } else if (this.$store.commit('setMyBookings')) {
        this.$store.commit('setMyBookings', []);
      }
      const identifier = this.bookingIdentifier;
      if (this.userEmail && this.sessionId) {
        this.$store.dispatch('get_my_bookings', identifier);
      }
    },
    formatPrice(price) {
      if (!price || isNaN(price)) return '0';
      return parseFloat(price).toLocaleString('en-KE', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatDate(date) {
      if (!date) return 'N/A';
      var parsedDate = this.$moment(date);
      if (!parsedDate.isValid()) return 'N/A';
      return parsedDate.format('MMM DD, YYYY');
    },
    formatDateTime(date) {
      if (!date) return 'N/A';
      var parsedDate = this.$moment(date);
      if (!parsedDate.isValid()) return 'N/A';
      return parsedDate.format('MMM DD, YYYY HH:mm');
    },
    reportDateFormat(date) {
      if (!date) return 'N/A';
      var parsedDate = this.$moment(date);
      if (!parsedDate.isValid()) return 'N/A';
      return parsedDate.format('YYYY-MM-DD HH:mm:ss');
    },
    getExportFileName(format) {
      return this.$moment().format('YYYY-MM-DD HH:mm:ss') + ' My Bookings Report.' + format;
    },
    filterByDateRange(bookings, range) {
      var now = this.$moment();
      var startDate = null;
      switch(range) {
        case 'This Week': startDate = now.clone().startOf('week'); break;
        case 'This Month': startDate = now.clone().startOf('month'); break;
        case 'Last 3 Months': startDate = now.clone().subtract(3, 'months').startOf('month'); break;
        case 'This Year': startDate = now.clone().startOf('year'); break;
        default: return bookings;
      }
      var _this = this;
      return bookings.filter(function(booking) {
        var createdDate = _this.$moment(booking.created_at);
        return createdDate.isAfter(startDate);
      });
    },
    getAccommodationNames(booking) {
      if (booking.facility_bookings && booking.facility_bookings.length > 0) {
        var names = booking.facility_bookings.map(function(f) { return f.facility_name; });
        return names.join(', ');
      }
      return 'N/A';
    },
    getActivityNames(booking) {
      if (booking.inventory_bookings && booking.inventory_bookings.length > 0) {
        var names = booking.inventory_bookings.map(function(i) { return i.inventory_name; });
        return names.join(', ');
      }
      return 'N/A';
    },
    getBookingStatus(booking) {
      if (booking.booking_status_display) return booking.booking_status_display;
      if (booking.booking_status) return booking.booking_status;
      if (booking.status_display) return booking.status_display;
      if (booking.status) return booking.status;
      return 'Confirmed';
    },
    getStatusStyle(booking) {
      var status = this.getBookingStatus(booking);
      var colors = {
        'Checked Out': '#6c757d',
        'Checked In': '#28a745',
        'Cancelled': '#dc3545',
        'Confirmed': '#28a745',
        'Unconfirmed': '#6c757d',
        'Pending': '#007bff',
        'No-Show': '#dc3545',
        'Deposit': '#ffc107',
        'To Invoice': '#fd7e14',
        'Abandoned': '#6c757d',
        'Updated': '#17a2b8',
        'Complimentary': '#20c997',
        'Postponed': '#fd7e14'
      };
      var color = colors[status] || '#007bff';
      return 'background-color: ' + color + '; color: #fff; padding: 4px 8px; border-radius: 4px; display: inline-block; font-size: 12px; font-weight: 500;';
    },
    getMore() {
      this.loading = true;
      this.$store.dispatch('loading_true');
      var data = { identifier: this.bookingIdentifier, page: this.page };
      var _this = this;
      if (this.page === 2) {
        this.$store.dispatch('get_my_bookings_streamed', data).finally(function() {
          _this.loading = false;
          _this.page = _this.page + 1;
          _this.$store.dispatch('loading_false');
        });
      } else {
        this.$store.dispatch('get_my_bookings_streaming', data).finally(function() {
          _this.loading = false;
          _this.page = _this.page + 1;
          _this.$store.dispatch('loading_false');
        });
      }
    },
    getAllBookings() {
      this.loading = true;
      this.$store.dispatch('loading_true');
      var _this = this;
      this.$store.dispatch('get_all_my_bookings', this.bookingIdentifier)
        .finally(function() {
          _this.loading = false;
          _this.$store.dispatch('loading_false');
        });
    },
    viewBooking(publicId) {
      this.$router.push({ name: 'Booking', params: { booking_id: publicId } });
    }
  }
}
</script>

<style scoped>
.payment-container {
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
}
.payment-container:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}
.card-header {
  background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
  padding: 16px;
  border-radius: 8px;
}
.earth-color {
  color: #587e27;
  font-weight: 600;
  margin: 0;
  font-size: 1.5rem;
}
.title-decoration {
  height: 4px;
  background: linear-gradient(90deg, #587e27, transparent);
  margin-bottom: 16px;
  width: 100px;
}
.futuristic-card {
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.3s ease;
  margin-top: 8px;
}
.futuristic-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}
.futuristic-expansion {
  border-radius: 8px;
  background: #ffffff;
  transition: all 0.3s ease;
  margin-bottom: 16px;
}
.futuristic-expansion:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}
.futuristic-input >>> .v-input__slot {
  border-radius: 8px;
  background: #f5f7fa;
}
.futuristic-search {
  max-width: 300px;
}
.futuristic-search >>> .v-input__slot {
  border-radius: 8px;
  background: #f5f7fa;
}
.futuristic-btn {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  transition: all 0.2s ease;
}
.futuristic-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.futuristic-table {
  border-radius: 8px;
  background: #ffffff;
}
.futuristic-table >>> thead tr th {
  background: linear-gradient(45deg, #e8eaf6, #f5f7fa);
  color: #587e27;
  font-weight: 600;
  font-size: 13px;
}
.futuristic-table >>> tbody tr:hover {
  background-color: #f5f7fa;
}
.futuristic-link {
  color: #587e27;
  text-decoration: none;
  transition: color 0.2s ease;
}
.futuristic-link:hover {
  color: #1b5e20;
}
.futuristic-alert {
  border-radius: 8px;
  background: #fff5f5;
}
.identifier-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 4px;
}
.badge {
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  background: #f0f0f0;
  color: #333;
}
.badge-session {
  background: #e3f2fd;
  color: #0d47a1;
}
.badge-email {
  background: #e8f5e9;
  color: #1b5e20;
}
@media (max-width: 960px) {
  .payment-container { padding: 12px; }
  .card-header { padding: 12px; }
  .earth-color { font-size: 1.25rem; }
  .futuristic-btn { font-size: 0.9rem; }
  .futuristic-search { max-width: 200px; }
}
@media (max-width: 600px) {
  .payment-container { padding: 8px; border-radius: 6px; }
  .card-header { padding: 8px; }
  .earth-color { font-size: 1.1rem; }
  .futuristic-btn { font-size: 0.85rem; }
  .futuristic-table >>> thead { display: none; }
  .futuristic-table >>> tbody tr { display: block; margin-bottom: 16px; border: 1px solid #e0e0e0; border-radius: 8px; }
  .futuristic-table >>> tbody tr td { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-bottom: 1px solid #e0e0e0; }
  .futuristic-table >>> tbody tr td:last-child { border-bottom: none; }
  .futuristic-table >>> tbody tr td::before { content: attr(data-label); font-weight: 600; color: #587e27; margin-right: 16px; min-width: 100px; }
  .futuristic-search { max-width: 100%; margin-top: 8px; }
  .card-header { flex-direction: column; align-items: stretch; }
}
</style>